import {
  CreateMaterialUploadRequest,
  MATERIAL_MAX_BYTES,
  MaterialUploadResponse,
} from '@quizdb/common';
import { problemCode } from '../api/problemCode';

/** A PDF study material that has finished uploading straight to S3 (QZ-D69). */
export interface StudyMaterialUpload {
  key: string;
  name: string;
  sizeBytes: number;
}

/** `uploadMaterial`'s failure modes. The page shows `t(`error.${code}`)` (PDG-D9). */
export class MaterialUploadError extends Error {
  readonly code: 'InvalidMaterial' | 'unknown';

  constructor(code: 'InvalidMaterial' | 'unknown') {
    super(code);
    this.name = 'MaterialUploadError';
    this.code = code;
  }
}

/** Accepts a declared `application/pdf` type, or an empty type with a `.pdf` name. */
const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || (file.type === '' && /\.pdf$/i.test(file.name));

/**
 * Validates `file`, asks `createUpload` for a presigned PUT and uploads straight to S3
 * (QZ-D69, PDG-D6). `createUpload` is the page's
 * `(request) => createMaterialUpload(request).unwrap()`, so this module needs no store.
 */
export const uploadMaterial = async (
  file: File,
  createUpload: (request: CreateMaterialUploadRequest) => Promise<MaterialUploadResponse>
): Promise<StudyMaterialUpload> => {
  if (!isPdfFile(file) || file.size <= 0 || file.size > MATERIAL_MAX_BYTES) {
    throw new MaterialUploadError('InvalidMaterial');
  }

  let upload: MaterialUploadResponse;
  try {
    upload = await createUpload({
      name: file.name,
      sizeBytes: file.size,
      contentType: 'application/pdf',
    });
  } catch (thrown) {
    const code = problemCode(thrown) === 'InvalidMaterial' ? 'InvalidMaterial' : 'unknown';
    throw new MaterialUploadError(code);
  }

  // Plain `fetch`, not RTK Query: `quizdbApi`'s `prepareHeaders` adds a bearer token, and S3
  // refuses a presigned request that also carries an `Authorization` header (PDG-D6). No
  // `Content-Length`: the browser derives it from `file`, and the URL is signed with that size.
  let response: Response;
  try {
    response = await fetch(upload.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });
  } catch {
    throw new MaterialUploadError('unknown');
  }
  if (!response.ok) {
    throw new MaterialUploadError('unknown');
  }

  return { key: upload.key, name: file.name, sizeBytes: file.size };
};
